import HooksPoster from 'data/HooksPoster';
import SendGridPoster from 'data/SendGridPoster';
import type { Bundle, BundleEntry, CarePlan, Identifier, Questionnaire } from 'fhir/r4';
import {
  REFERRAL_SURVEYS,
  REFERRAL_SUBJECT_LINES,
  EMAIL_TEMPLATES,
  TEXT_MESSAGE_TEMPLATES,
} from 'fhir-sdoh/common/constants';
import * as fhirpath from 'fhirpath';
import moment from 'moment';
import { useEffect } from 'react';
import React from 'react';
import { List, Label, Loader, Popup, Grid, Button, Icon } from 'semantic-ui-react';
import type { ReferralSummary } from 'types';

import { useAuthContext } from '../../../../../contexts/AuthContext';
import { Screen, FHIRProxyClient } from '../../../../../fhir-sdoh';
import type { FlatCondition } from '../../../../../types/flat-fhir';
import * as FHIRUtil from '../../../../../utils/FHIRUtils';
import { mapKey } from '../../../../../utils/mapKey';
import type {
  StepCarePlanContext,
  StepConditionRaw,
  StepMutate,
  StepPatientIdentity,
} from '../../types';

/**
 * Props for {@link Screening}, as passed by `careplan/steps/index.tsx`.
 *
 * `condition` is the RAW FHIR `Condition` here — this child calls
 * `parseFHIRCondition` on it itself — unlike `Assessment` and `Intervention`,
 * which receive the already-flattened view. See `careplan/types.ts`.
 */
type ScreeningProps = StepPatientIdentity &
  StepCarePlanContext & {
    /** The raw screening `Condition`; flattened below rather than by the parent. */
    condition?: StepConditionRaw;
    /** The whole `/CarePlan/?patient=...` bundle, read for the screening-status identifier. */
    carePlanData?: Bundle | undefined;
    /** The org's questionnaire bundle, searched for the screening questionnaire. */
    questionnaireData?: Bundle | undefined;
    /**
     * Active + completed referrals, forwarded from `patient/index.tsx` for the
     * HA primer survey.
     *
     * SUSPECTED DEFECT — the producer's state is SEEDED WITH `{}`, not an array.
     * `patient/index.tsx` declares
     * `useState<ReferralSummary[] | Record<string, never> | undefined>({})` for
     * `activeAndCompletedReferralData` and only replaces it once the profile
     * SWR `onSuccess` runs. `qualify` below guards with `props.referralData &&`,
     * which an empty object PASSES, and then calls `.forEach` on it — so
     * qualifying an HA member before the profile query resolves throws
     * "props.referralData.forEach is not a function" and no web survey is sent.
     *
     * Both sides' assumptions: this child assumes a truthy `referralData` is an
     * array; the producer guarantees an array only after its first successful
     * fetch and uses `{}` as its "not yet loaded" sentinel.
     *
     * Declared as the array this consumer actually requires rather than as the
     * producer's union, so the disagreement surfaces as ONE diagnostic at the
     * `steps/index.tsx` call site instead of being absorbed here. Reported, not
     * fixed: an `Array.isArray` guard would silently skip the survey send.
     */
    referralData?: ReferralSummary[] | undefined;
    /** Fires after qualify/disqualify settle, so the stepper re-reads. */
    onScreen: () => void;
    /**
     * Opens the survey sidebar for the screening questionnaire.
     *
     * Optional to match the two hubs it is forwarded through, but invoked
     * unguarded from the survey label's `onClick` below — `patient/index.tsx`
     * always supplies a real function.
     */
    toggleSurveySidebar?: ((questionnaireId: string) => void) | undefined;
    /** Revalidates the email/SMS communication log. */
    communicationsMutate: StepMutate;
    /** Revalidates the profile observation query. */
    profileMutate?: StepMutate | undefined;
    /** The parent's `refresh`, re-running every care-plan query. */
    refreshCareplanTasks: StepMutate;
  };

function Screening(props: ScreeningProps) {
  const [loading, setLoading] = React.useState(false);
  const [popupOpen, setPopupOpen] = React.useState(false);
  const [availableSurvey, setAvailableSurvey] = React.useState(false);
  // The `screening-status` identifier's `value` off the member's CarePlan —
  // 'Qualified' | 'Ineligible' | 'Declined' in practice, but the identifier is a
  // free-text FHIR value, so `string` rather than a literal union.
  const [screeningStatus, setScreeningStatus] = React.useState<string | null>(null);

  const handleOpen = () => {
    setPopupOpen(true);
  };

  const handleClose = () => {
    setPopupOpen(false);
  };

  const condition = props.condition;
  let flatCondition: FlatCondition | undefined;
  if (condition) {
    flatCondition = FHIRUtil.parseFHIRCondition(condition);
  }

  const screeningQuestionnaire = fhirpath
    .evaluate(props.planDefinition, "action.where(id = 'screening').definitionCanonical")
    .toString();
  const screeningQuestionnaireId = screeningQuestionnaire.substring(screeningQuestionnaire.indexOf('/') + 1);

  useEffect(() => {
    // `resource` is the `FhirResource` union, and only `Questionnaire` carries
    // `item`; the surrounding `Object.keys(...).includes('item')` is this code's
    // own runtime check for exactly that, so the narrowing states what the guard
    // already establishes rather than adding one.
    const questionnaire = (props.questionnaireData?.entry?.filter(
      (questionnaire: BundleEntry) => questionnaire?.resource?.id === screeningQuestionnaireId
    )[0] &&
      props.questionnaireData?.entry?.filter(
        (questionnaire: BundleEntry) => questionnaire?.resource?.id === screeningQuestionnaireId
      )?.[0]?.resource) as Questionnaire | undefined;
    if (questionnaire && Object.keys(questionnaire)?.includes('item') && questionnaire?.item?.length !== 0) {
      setAvailableSurvey(true);
    } else {
      setAvailableSurvey(false);
    }
  }, [screeningQuestionnaireId, props.questionnaireData]);

  useEffect(() => {
    // SUSPECTED DEFECT — the optional chain stops short of `.length`:
    // `props?.carePlanData?.entry.length` guards `carePlanData` but then reads
    // `.length` off `entry` unguarded, so a `/CarePlan` bundle that arrives with
    // no `entry` (FHIR `Bundle.entry` is `0..*`) throws "Cannot read properties
    // of undefined (reading 'length')" inside this effect.
    //
    // Both sides' assumptions: this effect assumes a present `carePlanData` has
    // a present `entry`; the FHIR contract guarantees only the bundle.
    //
    // The `!` is on the `entry` read and must stay INSIDE the optional chain.
    // Wrapping the chain in parentheses to assert it — `(props?.carePlanData?.entry as T).length` —
    // reads as a pure type-level edit but TERMINATES the short-circuit, so the
    // `.length` is evaluated even when `carePlanData` is absent and the effect
    // throws on every render before the bundle arrives. `!` erases to nothing
    // and leaves the chain intact; the parenthesised assertion does not.
    // The `as number` wraps the whole chain rather than a link in it, so it erases
    // too — verified: this emits the same JavaScript as the unannotated chain.
    if ((props?.carePlanData?.entry!.length as number) > 0) {
      const carePlanPayload = { ...props?.carePlanData?.entry?.[0]?.resource } as Partial<CarePlan>;
      const carePlayPayloadIdentifier = carePlanPayload?.identifier;
      const careplanstatus = carePlayPayloadIdentifier?.find(
        (obj: Identifier) => obj.system === 'https://projectwell.io/fhir/identifiers/screening-status'
      );
      if (careplanstatus?.value) {
        setScreeningStatus(careplanstatus.value);
      } else {
        setScreeningStatus(null);
      }
    } else {
      setScreeningStatus(null);
    }
  }, [props.carePlanData]);

  const authInfo = useAuthContext();

  const intervention: string = fhirpath
    .evaluate(
      props.carePlan,
      "identifier.where(system = 'https://projectwell.io/fhir/identifiers/selected-intervention').value"
    )
    .toString();

  const qualify = async () => {
    setPopupOpen(false);

    //TODO: Call the Qualifier module defined in PlanDefinition. Check Screening Survey responses and othe criteria

    const proxyURL = import.meta.env.VITE_API_ENDPOINT + '/fhir/';
    const fhirProxyClient = new FHIRProxyClient(authInfo.token, proxyURL);

    setLoading(true);

    try {
      // `orgId`/`patientId` are optional on the props and `carePlan.id` is
      // `0..1` in FHIR, while `diagnoseFoodInsecurity` requires all three as
      // `string`. The `!`s record the existing reachability: `steps/index.tsx`
      // renders this child only inside `{carePlan && ...}`, and `props.carePlan.id`
      // already throws today when `carePlan` is absent. Defaulting any of them
      // would post a screening diagnosis against the wrong member or plan.
      await Screen.diagnoseFoodInsecurity(
        props.orgId!,
        props.patientId!,
        props.carePlan!.id!,
        moment().toISOString(),
        true,
        {
          reference: 'Practitioner/' + authInfo.practitionerId,
          display: authInfo.practitionerName,
        },
        authInfo.practitionerName,
        fhirProxyClient,
        'Qualified'
      ).then(async () => {
        const body = {
          patientId: props.patientId,
          status: 'ACTIVE',
          reason: 'ENROLL',
        };

        //Eg:  { "patientId": "aec0d80f424537563da44ba221f01908", "status":"INACTIVE" }

        await HooksPoster(`/hooks/patient/status`, authInfo.token, body).then((_data) => {
          // `profileMutate` is optional on the props because `steps/index.tsx`
          // forwards its own optional prop, but this call is unguarded today —
          // a `?.` here would silently skip the profile revalidation.
          props.profileMutate!();
          props.refreshCareplanTasks();
          // send hA primer email survey (#1)
          if (props.orgId === 'HA' && props.referralData) {
            const filteredReferralSurveys: string[] = [];
            const subject = 'HA PRIMER - ALL REFERRALS INTAKE + SURVEY';
            // SUSPECTED DEFECT — the `??` fallback pushes an OBJECT into a
            // `string[]`. `REFERRAL_SURVEYS['HA PRIMER - ALL REFERRALS INTAKE +
            // SURVEY']` is the per-referral-type map
            // `{ 'Pantry Kit', 'Monthly Nutritional Support', 'Monthly Prepared Meals' }`
            // (fhir-sdoh/common/constants.ts, REFERRAL_SURVEYS_LITERAL), not a
            // string — unlike its sibling keys, which are plain survey ids. So
            // for any `referral.type` outside those three the left operand is
            // `undefined` and the fallback evaluates to that whole map object,
            // which is then pushed as a `questionnaireId` and posted to
            // `/hooks/web-survey`.
            //
            // Both sides' assumptions: the caller believes `REFERRAL_SURVEYS[subject]`
            // is `string | Record<string, string>` and that the `as string` arm
            // covers the string case; the constant guarantees `Record<string,string>`
            // for THIS key, so the `as string` arm is unreachable as a string.
            //
            // Reported, not fixed: filtering the non-string out would drop web
            // surveys that are sent today for unmapped referral types.
            props.referralData.forEach((referral: ReferralSummary) => {
              // `ReferralSummary['type']` is `string | undefined`; the assertion
              // matches the one this code already makes on the next line. A
              // `.includes(undefined)` is simply false, which is today's result.
              if (!import.meta.env.VITE_HA_EXCLUDED_REFERRAL_TYPES?.split(',').includes(referral.type as string)) {
                const referralSurvey =
                  (REFERRAL_SURVEYS[subject] as Record<string, string>)?.[referral.type as string] ??
                  (REFERRAL_SURVEYS[subject] as unknown as string);
                // Kept as an expression statement: an `if` would emit different JavaScript.
                // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                !filteredReferralSurveys.includes(referralSurvey) && filteredReferralSurveys.push(referralSurvey);
              }
            });
            // Kept as an expression statement: an `if` would emit different JavaScript.
            // eslint-disable-next-line @typescript-eslint/no-unused-expressions
            filteredReferralSurveys.length > 0
              ? // The async callback's promise is dropped by `forEach`, as upstream wrote it.
                // eslint-disable-next-line @typescript-eslint/no-misused-promises
                filteredReferralSurveys.forEach(async (survey) => {
                  setLoading(true);
                  const payload: Record<string, unknown> = {
                    patientId: props.patientId,
                    subject: REFERRAL_SUBJECT_LINES[subject],
                    sender: authInfo.practitionerName,
                    memberName: props.patientName,
                    email: props.patientEmail,
                    emailTemplate: EMAIL_TEMPLATES[subject],
                    questionnaireId: survey,
                    emailTitle: subject,
                    text: 'screen launch',
                  };
                  if (props.patientPhone) {
                    // Same array-concatenation defect as KNOWN_BUGS.md CP-27: `patientPhone`
                    // is the raw fhirpath collection, so two numbers on file produce one
                    // malformed string. Cast, not indexed.
                    payload['phoneNumber'] = ('+1' + (props.patientPhone as string)).replaceAll('-', '');
                  }
                  await SendGridPoster(`/hooks/web-survey`, authInfo.token, payload).then(
                    () => {
                      props.communicationsMutate();
                    },
                    (e) => {
                      console.log(`Error: Failed to send web survey email(s): ${(e as Error).toString()}`);
                    }
                  );
                })
              : console.log('Error: No active referrals, unable to send web survey email(s)');
          }
          if (props.orgId === 'ZING') {
            let subject: string[] = [];
            let payload: Record<string, unknown> = {};
            subject = ['ZING WELCOME EMAIL', 'APP DOWNLOAD EMAIL'];
            for (let s = 0; s < subject.length; s++) {
              setLoading(true);
              payload = {
                patientId: props.patientId,
                subject: subject[s],
                sender: authInfo.practitionerName,
                dynamicTemplateData: {
                  memberName: (props.patientName as unknown[])[0],
                },
                to: (props.patientEmail as unknown[])[0],
                from: 'nourishedrx@nourishedrx.com',
                templateId: EMAIL_TEMPLATES[mapKey(subject[s])],
                text: 'screen launch',
              };
              SendGridPoster(`/hooks/mail`, authInfo.token, payload).then(
                () => {
                  props.communicationsMutate();
                },
                (e) => {
                  console.log(`Error: Failed to send web survey email(s): ${(e as Error).toString()}`);
                }
              );
              payload = {
                // `as string` rather than `mapKey(...)`: `'+1' + v` and
                // `'+1' + String(v)` agree on every value except a Symbol,
                // where the former throws and the latter does not. The
                // assertion keeps the existing concatenation untouched.
                phoneNumber: '+1' + ((props.patientPhone as unknown[])[0] as string),
                patientId: props.patientId,
                message: TEXT_MESSAGE_TEMPLATES[mapKey(subject[s])],
                subject: subject[s],
                sender: authInfo.practitionerName + ' (Program Enroll)',
                text: 'screen launch',
              };
              SendGridPoster(`/hooks/text`, authInfo.token, payload).then(
                () => {
                  props.communicationsMutate();
                },
                (e) => {
                  console.log(`Error: Failed to send text message(s): ${(e as Error).toString()}`);
                }
              );
            }
          }
          setPopupOpen(false);
        });
      });
    } catch (error) {
      console.log('Error in Screening Step():', (error instanceof Error ? error.message : String(error)));
      setLoading(false);
      throw error;
    }

    // Delaying intentionally to process all backend tasks
    setTimeout(() => {
      props.onScreen(); //callback
      setLoading(false);
    }, 4000);
  };

  const disqualify = async (reason: string) => {
    const proxyURL = import.meta.env.VITE_API_ENDPOINT + '/fhir/';
    const fhirProxyClient = new FHIRProxyClient(authInfo.token, proxyURL);

    setLoading(true);
    // Same reachability as `qualify` above.
    await Screen.diagnoseFoodInsecurity(
      props.orgId!,
      props.patientId!,
      props.carePlan!.id!,
      moment().toISOString(),
      false,
      {
        reference: 'Practitioner/' + authInfo.practitionerId,
        display: authInfo.practitionerName,
      },
      authInfo.practitionerName,
      fhirProxyClient,
      reason
    ).then(async () => {
      const body = {
        patientId: props.patientId,
        status: 'INACTIVE',
        reason: 'DISENROLL',
      };

      //Eg:  { "patientId": "aec0d80f424537563da44ba221f01908", "status":"INACTIVE" }
      await HooksPoster(`/hooks/patient/status`, authInfo.token, body).then((_data) => {
        // Unguarded today, as in `qualify`.
        props.profileMutate!();
        props.refreshCareplanTasks();
        setPopupOpen(false);
      });
    });
    // Delaying intentionally to process all backend tasks
    setTimeout(() => {
      props.onScreen(); //callback
      setLoading(false);
    }, 4000);
    //  await disqualifyPatient();
  };

  return (
    <>
      {loading && <Loader active inline="centered" />}
      <List horizontal>
        {flatCondition && (
          <>
            {flatCondition.verificationStatusCode === 'confirmed' && (
              <>
                <Popup
                  flowing
                  trigger={
                    <Label color="orange" as={!intervention ? 'a' : 'label'} image>
                      <Icon name="thumbs up" />
                      <Label.Detail as={!intervention ? 'a' : 'label'}>Qualified</Label.Detail>
                    </Label>
                  }
                  content={
                    <Grid divided columns="equal">
                      <Grid.Column>
                        <Popup
                          trigger={
                            <Button.Group size="mini" color="red">
                              <Button
                                size="mini"
                                color="red"
                                content="Ineligible"
                                onClick={() => disqualify('Ineligible')}
                              ></Button>
                              <Button.Or />
                              <Button
                                size="mini"
                                color="red"
                                content="Declined"
                                onClick={() => disqualify('Declined')}
                              ></Button>
                            </Button.Group>
                          }
                          content="Based on the Screening Questionnaire responses, this member is NOT QUALIFIED to this program."
                          position="top center"
                          size="tiny"
                          inverted
                        />
                      </Grid.Column>
                    </Grid>
                  }
                  open={popupOpen && !intervention}
                  onClose={handleClose}
                  onOpen={handleOpen}
                  on="click"
                />
              </>
            )}
            {flatCondition.verificationStatusCode !== 'confirmed' && (
              <>
                <Popup
                  flowing
                  trigger={
                    <Label color="red" as="a" image>
                      <Icon name="thumbs down" />
                      <Label.Detail as="a">Disqualified</Label.Detail>
                    </Label>
                  }
                  content={
                    <Grid divided columns="equal">
                      <Grid.Column>
                        <Popup
                          trigger={<Button size="mini" color="green" content="Qualified" fluid onClick={qualify} />}
                          content="Based on the Screening Questionnaire responses, this member is QUALIFIED to this program."
                          position="top center"
                          size="tiny"
                          inverted
                        />
                      </Grid.Column>
                      <Grid.Column>
                        <Popup
                          trigger={
                            <Button
                              size="mini"
                              color="red"
                              content={screeningStatus === 'Declined' ? 'Ineligible' : 'Declined'}
                              onClick={() => disqualify(screeningStatus === 'Declined' ? 'Ineligible' : 'Declined')}
                            ></Button>
                          }
                          content="Based on the Screening Questionnaire responses, this member is NOT QUALIFIED to this program."
                          position="top center"
                          size="tiny"
                          inverted
                        />
                      </Grid.Column>
                    </Grid>
                  }
                  open={popupOpen}
                  onClose={handleClose}
                  onOpen={handleOpen}
                  on="click"
                />
              </>
            )}
            <Popup
              flowing
              trigger={
                <Label color="grey" image>
                  {moment(flatCondition.onSetDate).fromNow()}
                </Label>
              }
              content={
                <Label>
                  By: {flatCondition.lastModifiedBy}.{' '}
                  {moment(flatCondition.lastUpdated).format('MMMM Do YYYY, h:mm:ss a')}
                </Label>
              }
              on="hover"
            />
          </>
        )}
        {!flatCondition && (
          <>
            <Label
              color={props.carePlan ? 'blue' : 'grey'}
              image
              className={availableSurvey ? '' : 'ui disabled button'}
            >
              <Icon name="file" />
              <Label.Detail as="a" onClick={() => props.toggleSurveySidebar!(screeningQuestionnaireId)}>
                {availableSurvey ? 'Take Survey' : 'No Available Survey'}
              </Label.Detail>
            </Label>

            <Popup
              flowing
              trigger={
                <Label color="blue" as="a" image>
                  <Icon name="stethoscope" />
                  <Label.Detail as="a">Qualify Member</Label.Detail>
                </Label>
              }
              content={
                <Grid flowing style={{ width: '90%' }} divided>
                  <Grid.Column width={7}>
                    <Popup
                      trigger={
                        <Button
                          size="mini"
                          color="green"
                          position="center"
                          content="Qualified"
                          fluid
                          onClick={qualify}
                        />
                      }
                      content="Based on the Screening Questionnaire responses, this member is QUALIFIED to this program."
                      position="top center"
                      size="tiny"
                      inverted
                    />
                  </Grid.Column>
                  <Grid.Column width={9}>
                    <Popup
                      trigger={
                        <Button.Group size="mini" color="red">
                          <Button
                            size="mini"
                            color="red"
                            content="Ineligible"
                            onClick={() => disqualify('Ineligible')}
                          ></Button>
                          <Button.Or />
                          <Button
                            size="mini"
                            color="red"
                            content="Declined"
                            onClick={() => disqualify('Declined')}
                          ></Button>
                        </Button.Group>
                      }
                      content="Based on the Screening Questionnaire responses, this member is NOT QUALIFIED to this program."
                      position="top center"
                      size="tiny"
                      inverted
                    />
                  </Grid.Column>
                </Grid>
              }
              open={popupOpen}
              onClose={handleClose}
              onOpen={handleOpen}
              on="click"
            />
          </>
        )}
      </List>
    </>
  );
}

export default Screening;
